/* eslint-disable max-lines-per-function */
'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import ArrowLeftSLineIcon from 'remixicon-react/ArrowLeftSLineIcon';
import ArrowRightSLineIcon from 'remixicon-react/ArrowRightSLineIcon';
import type { Swiper as SwiperType } from 'swiper';
import { A11y, Autoplay } from 'swiper/modules';
import 'swiper/css';
import { Swiper, SwiperSlide } from 'swiper/react';
import ResponsiveShopImage from './responsive-shop-image.component';

export interface TMainBanner {
  imageUrlDesktop: string;
  imageUrlMobile: string;
  link: string;
  alt?: string;
}

interface IMainBanner {
  slides: TMainBanner[];
  duration: number;
}

interface IResponsiveBannerImage {
  slide: TMainBanner;
  alt: string;
  priority?: boolean;
}

function getBannerLinkProps(href: string) {
  if (!href.startsWith('http://') && !href.startsWith('https://')) {
    return {};
  }

  return {
    target: '_blank' as const,
    rel: 'noopener noreferrer',
  };
}

function resolveSlideAlt(slide: TMainBanner, index: number) {
  return slide.alt?.trim() || `بنر تبلیغاتی ${index + 1} فروشگاه ویترو`;
}

function ResponsiveBannerImage({
  slide,
  alt,
  priority = false,
}: IResponsiveBannerImage) {
  return (
    <ResponsiveShopImage
      desktopSrc={slide.imageUrlDesktop}
      mobileSrc={slide.imageUrlMobile}
      alt={alt}
      priority={priority}
      quality={priority ? 70 : 65}
      mobileSizes="100vw"
      desktopSizes="100vw"
      className="select-none object-center"
    />
  );
}

export function MainBanner({ duration, slides }: IMainBanner) {
  const [swiper, setSwiper] = useState<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  if (!slides.length) return null;

  const hasMultipleSlides = slides.length > 1;

  return (
    <section
      aria-label="بنرهای اصلی فروشگاه ویترو"
      className="
        relative h-[200px] w-full overflow-hidden
        md:h-auto md:aspect-[1440/493]
      "
    >
      <Swiper
        // Keep LTR slide order so admin sortOrder (ترتیب) matches shop order,
        // even when the page itself is RTL. Loop+peek would also show the last
        // slide on the left of the first slide, which looks "reversed".
        dir="ltr"
        modules={[Autoplay, A11y]}
        onSwiper={setSwiper}
        onRealIndexChange={(instance) => {
          setActiveIndex(instance.realIndex);
        }}
        rewind={hasMultipleSlides}
        speed={550}
        autoplay={
          hasMultipleSlides
            ? {
                delay: duration,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }
            : false
        }
        breakpoints={{
          0: {
            slidesPerView: 'auto',
            spaceBetween: 8,
            centeredSlides: true,
          },
          768: {
            slidesPerView: 1,
            spaceBetween: 0,
            centeredSlides: false,
          },
        }}
        className="h-full w-full"
      >
        {slides.map((slide, index) => (
          <SwiperSlide
            key={`${slide.imageUrlDesktop}-${slide.imageUrlMobile}-${index}`}
            className="
              !flex !h-full !w-[calc(100%_-_32px)]
              items-center justify-center
              md:!block md:!w-full
            "
          >
            {({ isActive }) => (
              <div
                className={`
                  relative w-full
                  origin-center
                  overflow-hidden
                  rounded-[6px]
                  transition-[height]
                  duration-[550ms]
                  ease-out
                  md:h-full
                  md:rounded-none

                  ${isActive ? 'h-[200px]' : 'h-[173px]'}
                `}
              >
                <Link
                  href={slide.link}
                  className="relative block h-full w-full"
                  {...getBannerLinkProps(slide.link)}
                >
                  <ResponsiveBannerImage
                    slide={slide}
                    alt={resolveSlideAlt(slide, index)}
                    priority={index === 0}
                  />
                </Link>
              </div>
            )}
          </SwiperSlide>
        ))}
      </Swiper>

      {hasMultipleSlides && (
        <>
          <div
            dir="ltr"
            className="
              absolute bottom-8 left-1/2 z-20
              hidden -translate-x-1/2 items-center gap-[6px]
              md:flex
            "
          >
            {slides.map((_, index) => {
              const isActive = activeIndex === index;

              return (
                <button
                  key={index}
                  type="button"
                  aria-label={`رفتن به اسلاید ${index + 1}`}
                  aria-current={isActive ? 'true' : undefined}
                  onClick={() => swiper?.slideTo(index)}
                  className={`
                    h-2 w-2 rounded-full
                    transition-all duration-200
                    focus-visible:outline-2
                    focus-visible:outline-offset-4
                    focus-visible:outline-black-default
                    ${isActive ? 'bg-silver-50' : 'bg-white/70'}
                  `}
                />
              );
            })}
          </div>

          <div
            dir="ltr"
            className="
              absolute bottom-8 right-[120px] z-20
              hidden items-center gap-6
              md:flex
            "
          >
            <button
              type="button"
              aria-label="اسلاید قبلی"
              onClick={() => swiper?.slidePrev()}
              className="
                flex size-10 items-center justify-center
                rounded-full bg-white text-black-500
                shadow-[0_2px_12.6px_rgba(157,157,157,0.2)]
                transition-transform
                hover:scale-105
                active:scale-95
                focus-visible:outline-2
                focus-visible:outline-offset-4
                focus-visible:outline-black-default
              "
            >
              <ArrowLeftSLineIcon size={24} />
            </button>
            <button
              type="button"
              aria-label="اسلاید بعدی"
              onClick={() => swiper?.slideNext()}
              className="
                flex size-10 items-center justify-center
                rounded-full bg-white text-black-500
                shadow-[0_2px_12.6px_rgba(157,157,157,0.2)]
                transition-transform
                hover:scale-105
                active:scale-95
                focus-visible:outline-2
                focus-visible:outline-offset-4
                focus-visible:outline-black-default
              "
            >
              <ArrowRightSLineIcon size={24} />
            </button>
          </div>
        </>
      )}
    </section>
  );
}
